import { useEffect, useState } from 'react';
import {
  mapFindAllASTFavoritesResponseType,
  mapFindAllTicketSalesResponseType,
  mapFindSavedASTResponseType,
  mapPaginationType,
  PaginationType,
  useCreateASTFavorite,
  useFindAllASTFavorites,
  useFindAllTicketSales,
  useFindSavedAST,
} from '@api';
import {
  getTableParams,
  QueryBuilderAstNodeType,
  QueryBuilderEntityConfigType,
  QueryBuilderShareQueryConfigType,
  useNotification,
} from '@common';
import { keepPreviousData } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { Route } from 'src/routes/_rootLayout/projects/sales';
import {
  useDeleteAstFavorite,
  useShareQuery,
  useTicketSalesQueryEntities,
} from './hooks';

const TRANS_PATH_NOTIFICATION = 'NOTIFICATIONS.LOAD_SHARED_QUERY';

type UseTicketSalesReturnType = {
  queryAst: QueryBuilderAstNodeType | null;
  initialAst: QueryBuilderAstNodeType | undefined;
  entities: QueryBuilderEntityConfigType[];
  savedQueries: ReturnType<typeof mapFindAllASTFavoritesResponseType>;
  results: ReturnType<typeof mapFindAllTicketSalesResponseType>['results'];
  pagination: PaginationType;
  isFetching: boolean;
  handleQueryAstChange: (ast: QueryBuilderAstNodeType | null) => void;
  handleSaveQuery: (name: string, ast: QueryBuilderAstNodeType) => void;
  shareQuery: QueryBuilderShareQueryConfigType;
  handleDeleteSavedQuery: (id: string) => void;
};

export const useTicketSales = (): UseTicketSalesReturnType => {
  const search = Route.useSearch();
  const { sharedAst: sharedAstId } = search;
  const tableParams = getTableParams(search);
  const navigate = Route.useNavigate();
  const { showError } = useNotification();

  const [queryAst, setQueryAst] = useState<QueryBuilderAstNodeType | null>(
    null
  );

  const entities = useTicketSalesQueryEntities();
  const { mutate: createFavorite } = useCreateASTFavorite();
  const { handleDeleteAstFavorite: handleDeleteSavedQuery } =
    useDeleteAstFavorite();
  const { shareQuery } = useShareQuery();

  const { data: savedQueries = [] } = useFindAllASTFavorites(
    { context: 'TICKET_SALES' },
    {
      query: {
        select: (data) => mapFindAllASTFavoritesResponseType(data),
      },
    }
  );

  const {
    data: initialAst,
    isError: isSharedAstError,
    error: sharedAstError,
  } = useFindSavedAST(
    sharedAstId ?? '',
    { context: 'TICKET_SALES' },
    {
      query: {
        enabled: !!sharedAstId,
        select: (data): QueryBuilderAstNodeType =>
          mapFindSavedASTResponseType(data),
      },
    }
  );

  useEffect(() => {
    if (!isSharedAstError) return;

    showError({
      title: `${TRANS_PATH_NOTIFICATION}.ERROR.TITLE`,
      message: `${TRANS_PATH_NOTIFICATION}.ERROR.MESSAGE`,
      error: sharedAstError as AxiosError,
    });
    navigate({ search: {}, replace: true });
  }, [isSharedAstError, sharedAstError, showError, navigate]);

  const {
    data: ticketSales = {
      results: [],
      pagination: mapPaginationType(undefined),
    },
    isFetching,
  } = useFindAllTicketSales(
    {
      ticketSalesAST: queryAst ?? {},
    },
    tableParams,
    {
      query: {
        placeholderData: keepPreviousData,
        select: (data) => mapFindAllTicketSalesResponseType(data),
      },
    }
  );

  const handleQueryAstChange = (ast: QueryBuilderAstNodeType | null): void => {
    setQueryAst(ast);

    if (ast === null && sharedAstId) {
      navigate({ search: {}, replace: true });
    }
  };

  const handleSaveQuery = (
    name: string,
    ast: QueryBuilderAstNodeType
  ): void => {
    createFavorite({
      data: { name, ast, context: 'TICKET_SALES' },
    });
  };

  return {
    queryAst,
    initialAst,
    entities,
    savedQueries,
    results: ticketSales.results,
    pagination: ticketSales.pagination,
    isFetching,
    handleQueryAstChange,
    handleSaveQuery,
    shareQuery,
    handleDeleteSavedQuery,
  };
};
